/**
 * Validation for a proposed chapter breakdown — pure, and deliberately the same
 * rules the pipeline enforces at load time.
 *
 * `igcse_prep.config._validate_subject` refuses a config whose chapters overlap.
 * Discovering that after an upload has been accepted means the student has waited
 * for an ingest that was never going to run, so the same rule runs here, before
 * anything is spent.
 */

import type { DetectedChapter, SourceDocument } from "./types";

export type IssueLevel = "error" | "warning";

export interface Issue {
  level: IssueLevel;
  /** Chapter number the issue attaches to, or null for a document-level issue. */
  chapter: number | null;
  message: string;
  /** The field the error is in, so the row can mark that box. */
  field?: "start" | "end";
  /** A one-tap correction, offered only when it leaves the chapter valid. */
  fix?: { label: string; startPage?: number; endPage?: number };
}

export interface Coverage {
  assigned: number;
  total: number;
  unassigned: number;
}

/** Confidence at or below this is surfaced for confirmation rather than trusted. */
export const UNCERTAIN_BELOW = 0.75;

function byStartPage(chapters: DetectedChapter[]): DetectedChapter[] {
  return [...chapters].sort((a, b) => a.startPage - b.startPage);
}

export function validate(doc: SourceDocument): Issue[] {
  const issues: Issue[] = [];

  if (doc.chapters.length === 0) {
    return [{ level: "error", chapter: null, message: "No chapters were detected." }];
  }

  const ordered = byStartPage(doc.chapters);
  for (const chapter of doc.chapters) {
    if (chapter.endPage < chapter.startPage) {
      const next = ordered.find((c) => c.startPage > chapter.startPage);
      const end = next ? next.startPage - 1 : null;
      issues.push({
        level: "error",
        chapter: chapter.number,
        message: `Ends on page ${chapter.endPage}, before it starts on page ${chapter.startPage}.`,
        field: "end",
        ...(end !== null && end >= chapter.startPage
          ? { fix: { label: `End at ${end}`, endPage: end } }
          : {}),
      });
    }
    if (chapter.startPage < 1 || chapter.endPage > doc.pageCount) {
      const clamped = chapter.endPage > doc.pageCount && chapter.startPage >= 1;
      issues.push({
        level: "error",
        chapter: chapter.number,
        message: `Pages ${chapter.startPage}-${chapter.endPage} fall outside the document's ${doc.pageCount} pages.`,
        field: chapter.startPage < 1 ? "start" : "end",
        ...(clamped && chapter.startPage <= doc.pageCount
          ? { fix: { label: `End at ${doc.pageCount}`, endPage: doc.pageCount } }
          : {}),
      });
    }
    if (chapter.title.trim() === "") {
      issues.push({
        level: "warning",
        chapter: chapter.number,
        message: "Has no title.",
      });
    }
    // An unsure start is asked as a question above the list (questions.ts), not noted here.
  }

  // Overlaps are what the pipeline refuses outright, so they are errors here too.
  for (let i = 1; i < ordered.length; i++) {
    const previous = ordered[i - 1]!;
    const current = ordered[i]!;
    if (current.startPage <= previous.endPage) {
      issues.push({
        level: "error",
        chapter: current.number,
        message: `Pages ${current.startPage}-${current.endPage} overlap chapter ${previous.number} (${previous.startPage}-${previous.endPage}).`,
        field: "start",
        ...(previous.endPage + 1 <= current.endPage
          ? { fix: { label: `Start at ${previous.endPage + 1}`, startPage: previous.endPage + 1 } }
          : {}),
      });
    } else if (
      current.startPage > previous.endPage + 1 &&
      current.startPage - previous.endPage - 1 < 3
    ) {
      // A gap of three or more pages is asked as a question instead (questions.ts).
      const gap = current.startPage - previous.endPage - 1;
      issues.push({
        level: "warning",
        chapter: current.number,
        message: `${gap} page${gap === 1 ? "" : "s"} between this and chapter ${previous.number} belong to no chapter and get no flashcards.`,
      });
    }
  }

  return issues;
}

export function coverage(doc: SourceDocument): Coverage {
  const pages = new Set<number>();
  for (const chapter of doc.chapters) {
    for (
      let p = Math.max(1, chapter.startPage);
      p <= Math.min(doc.pageCount, chapter.endPage);
      p++
    ) {
      pages.add(p);
    }
  }
  return {
    assigned: pages.size,
    total: doc.pageCount,
    unassigned: Math.max(0, doc.pageCount - pages.size),
  };
}

export function blocking(issues: Issue[]): Issue[] {
  return issues.filter((issue) => issue.level === "error");
}

/**
 * What ingest will cost the student, in their terms.
 *
 * Spike 002 established that a chapter is one unit of work: 15 pages transcribed
 * in ~36 seconds, one Lambda invocation per chapter, fanned out in parallel. The
 * estimate is therefore per chapter, not per page, and the wall-clock figure
 * assumes the fan-out rather than a serial pass.
 */
export const SECONDS_PER_CHAPTER = 40;
export const PARALLEL_CHAPTERS = 8;

export interface IngestEstimate {
  chapters: number;
  /** Chapters marked "skip for now": kept, not counted. */
  skipped: number;
  pages: number;
  seconds: number;
}

export function estimateIngest(doc: SourceDocument): IngestEstimate {
  const included = doc.chapters.filter((c) => c.included ?? true);
  const waves = Math.ceil(included.length / PARALLEL_CHAPTERS);
  return {
    chapters: included.length,
    skipped: doc.chapters.length - included.length,
    pages: coverage({ ...doc, chapters: included }).assigned,
    seconds: waves * SECONDS_PER_CHAPTER,
  };
}
